/** @jsxImportSource @emotion/react */

import React, { ReactNode } from 'react';
import { useTheme } from '../../ThemeProvider/ThemeProvider';
import { LinearLayout } from '../LinearLayout/LinearLayout';
import { SideNavigationButton } from './SideNavigationButton';
import AutoOrientationService from '../../../services/autoOrientation';
import { CSSProp } from '../../../types/emotion';

export interface SideNavigationProps {
  /**
   * The title of the side navigation.
   */
  title?: ReactNode;
  /**
   * The content of the side navigation.
   */
  children: ReactNode;
  /**
   * Whether the side navigation is expanded.
   */
  expanded: boolean;
  /**
   * Callback fired when the side navigation is expanded or collapsed.
   */
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * The theme of the side navigation.
   */
  theme?: 'dark' | 'light';
}

export function SideNavigation({
  title,
  children,
  expanded,
  onExpandedChange,
  theme = 'dark',
  ...props
}: SideNavigationProps & Omit<Partial<React.HTMLAttributes<HTMLDivElement>>, keyof SideNavigationProps> & CSSProp) {
  const { tokens } = useTheme();

  return (
    <div
      css={{
        background: theme === 'light' ? 'white' : tokens.colors['secondary-base-800'],
        paddingBottom: '0.75rem',
        color: theme === 'light' ? 'black' : 'white',
        overflow: 'hidden',
        transition: 'width 0.2s ease-in-out',
        ...AutoOrientationService.getStyles(
          { position: 'absolute', top: 0, left: 0, zIndex: 99, width: expanded ? '100vw' : 0 },
          { position: 'static', width: expanded ? '24rem' : '3rem' },
        ),
      }}
      {...props}
    >
      <LinearLayout className={`side-navigation-${theme}`} spacing="small" css={{ height: '100%' }}>
        <LinearLayout spacing="none" align="center" css={{ flexDirection: 'row-reverse !important' as 'row-reverse' }}>
          <SideNavigationButton
            data-testid="dssf-button-close-side-navigation"
            icon={expanded ? 'hide-sidebar' : 'show-sidebar'}
            onClick={() => onExpandedChange?.(!expanded)}
            onMobileNavigationClose={() => onExpandedChange?.(false)}
            css={{ width: 'auto' }}
          />
          <div css={{ flex: 1, overflow: 'hidden' }}>{title}</div>
        </LinearLayout>
        <div css={{ flex: 1, overflow: 'hidden' }}>{children}</div>
      </LinearLayout>
    </div>
  );
}
